import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "@/lib/supabaseClient";

/**
 * Centralized Supabase-backed AuthProvider.
 *
 * Design (Slice 5B-2D deadlock fix):
 *   - onAuthStateChange callback is synchronous, returns undefined,
 *     and issues NO Supabase queries.
 *   - The callback publishes a `profileRequest` state token
 *     ({ userId, generation }) which the SOLE profile-fetch effect
 *     consumes. Only that effect issues supabase.from('profiles').
 *   - Refs track lifecycle, generation, current user id, latest session,
 *     init watchdog and profile watchdog handles.
 *   - Two watchdogs:
 *       * init watchdog: fail-closed if no first auth event arrives.
 *       * profile watchdog: per-profileRequest ceiling.
 *   - refreshProfile() bumps generation and publishes a new
 *     profileRequest, then returns a Promise that resolves when THAT
 *     generation settles (success / missing / error / timeout / cleanup).
 *   - platformRole is derived as profile?.platform_role || null.
 *     Missing / failed / timed-out profile => platformRole=null. No
 *     default privileged role is ever fabricated.
 */

const INIT_WATCHDOG_MS = 8000;
const PROFILE_WATCHDOG_MS = 8000;

const AuthCtx = createContext(null);

async function fetchProfile(userId) {
  if (!userId || !supabase) return null;
  const { data, error } = await supabase
    .from("profiles")
    .select("id, email, full_name, platform_role, created_at")
    .eq("id", userId)
    .maybeSingle();
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("profile fetch error:", error.message);
    return null;
  }
  return data;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileRequest, setProfileRequest] = useState(null);

  const mountedRef = useRef(true);
  const genRef = useRef(0);
  const userIdRef = useRef(null);
  const sessionRef = useRef(null);
  const firstAuthEventReceivedRef = useRef(false);
  const initWatchdogRef = useRef(null);
  const profileWatchdogRef = useRef(null);
  const settleResolversRef = useRef(new Map());

  const resolveSettlement = useCallback((generation) => {
    const m = settleResolversRef.current;
    const r = m.get(generation);
    if (r) {
      m.delete(generation);
      try { r(); } catch (_) { /* noop */ }
    }
  }, []);

  const clearInitWatchdog = useCallback(() => {
    if (initWatchdogRef.current !== null) {
      clearTimeout(initWatchdogRef.current);
      initWatchdogRef.current = null;
    }
  }, []);

  const clearProfileWatchdog = useCallback(() => {
    if (profileWatchdogRef.current !== null) {
      clearTimeout(profileWatchdogRef.current);
      profileWatchdogRef.current = null;
    }
  }, []);

  const handleAuthEvent = useCallback((event, s) => {
    if (!mountedRef.current) return undefined;
    const nextUserId = s?.user?.id ?? null;
    const sameUser = nextUserId !== null && nextUserId === userIdRef.current;

    if (!firstAuthEventReceivedRef.current) {
      firstAuthEventReceivedRef.current = true;
      clearInitWatchdog();
    }

    if (event === "SIGNED_OUT" || nextUserId === null) {
      genRef.current += 1;
      userIdRef.current = null;
      sessionRef.current = null;
      clearProfileWatchdog();
      setSession(null);
      setProfile(null);
      setProfileRequest(null);
      setLoading(false);
      return undefined;
    }

    if (event === "TOKEN_REFRESHED" && sameUser) {
      sessionRef.current = s;
      setSession(s);
      return undefined;
    }
    if (event === "SIGNED_IN" && sameUser) {
      sessionRef.current = s;
      setSession(s);
      return undefined;
    }

    if (event === "USER_UPDATED" && sameUser) {
      const g = ++genRef.current;
      sessionRef.current = s;
      clearProfileWatchdog();
      setSession(s);
      setLoading(true);
      setProfileRequest({ userId: nextUserId, generation: g });
      return undefined;
    }

    // New authenticated user path.
    const g = ++genRef.current;
    userIdRef.current = nextUserId;
    sessionRef.current = s;
    clearProfileWatchdog();
    setSession(s);
    setProfile(null);
    setLoading(true);
    setProfileRequest({ userId: nextUserId, generation: g });
    return undefined;
  }, [clearInitWatchdog, clearProfileWatchdog]);

  useEffect(() => {
    mountedRef.current = true;
    const resolvers = settleResolversRef.current;

    if (!supabase) {
      setLoading(false);
      return () => { mountedRef.current = false; };
    }

    initWatchdogRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      if (firstAuthEventReceivedRef.current) return;
      initWatchdogRef.current = null;
      setSession(null);
      setProfile(null);
      setProfileRequest(null);
      setLoading(false);
    }, INIT_WATCHDOG_MS);

    let sub;
    try {
      const registration = supabase.auth.onAuthStateChange(handleAuthEvent);
      sub = registration?.data;
    } catch (_e) {
      clearInitWatchdog();
      setSession(null);
      setProfile(null);
      setProfileRequest(null);
      setLoading(false);
      return () => { mountedRef.current = false; };
    }

    return () => {
      mountedRef.current = false;
      clearInitWatchdog();
      clearProfileWatchdog();
      for (const [, r] of resolvers) { try { r(); } catch (_) { /* noop */ } }
      resolvers.clear();
      sub?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!profileRequest) return undefined;
    const { userId, generation } = profileRequest;
    let cancelled = false;

    clearProfileWatchdog();
    profileWatchdogRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      if (genRef.current !== generation) return;
      if (userIdRef.current !== userId) return;
      const captured = generation;
      genRef.current += 1;
      profileWatchdogRef.current = null;
      setProfile(null);
      setLoading(false);
      setProfileRequest((cur) =>
        cur && cur.generation === captured ? null : cur
      );
      resolveSettlement(captured);
    }, PROFILE_WATCHDOG_MS);

    fetchProfile(userId).then((data) => {
      if (cancelled) return;
      if (!mountedRef.current) return;
      if (genRef.current !== generation) return;
      if (userIdRef.current !== userId) return;
      clearProfileWatchdog();
      setProfile(data ?? null);
      setLoading(false);
      setProfileRequest((cur) =>
        cur && cur.generation === generation ? null : cur
      );
      resolveSettlement(generation);
    }).catch(() => {
      if (cancelled) return;
      if (!mountedRef.current) return;
      if (genRef.current !== generation) return;
      if (userIdRef.current !== userId) return;
      clearProfileWatchdog();
      setProfile(null);
      setLoading(false);
      setProfileRequest((cur) =>
        cur && cur.generation === generation ? null : cur
      );
      resolveSettlement(generation);
    });

    return () => {
      cancelled = true;
      clearProfileWatchdog();
      resolveSettlement(generation);
    };
  }, [profileRequest, clearProfileWatchdog, resolveSettlement]);

  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }, []);

  const signUp = useCallback(async ({ email, password, full_name, company }) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name, company } },
    });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    if (!mountedRef.current) return;
    genRef.current += 1;
    userIdRef.current = null;
    sessionRef.current = null;
    clearProfileWatchdog();
    setSession(null);
    setProfile(null);
    setProfileRequest(null);
    setLoading(false);
  }, [clearProfileWatchdog]);

  const resetPassword = useCallback(async (email) => {
    const redirectTo = `${window.location.origin}/update-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return data;
  }, []);

  const refreshProfile = useCallback(() => {
    const uid = sessionRef.current?.user?.id ?? null;
    if (!uid || !mountedRef.current) return Promise.resolve();
    const g = ++genRef.current;
    clearProfileWatchdog();
    setLoading(true);
    const p = new Promise((resolve) => {
      settleResolversRef.current.set(g, resolve);
    });
    setProfileRequest({ userId: uid, generation: g });
    return p;
  }, [clearProfileWatchdog]);

  const value = useMemo(() => ({
    user: session?.user || null,
    session,
    profile,
    platformRole: profile?.platform_role || null,
    loading,
    signIn, signUp, signOut, resetPassword, updatePassword, refreshProfile,
  }), [session, profile, loading, signIn, signUp, signOut, resetPassword, updatePassword, refreshProfile]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
